<template>
  <mf-loading-dialog :loading="loading">
    <base-page-layout title="Resultados do Destaque">
      <template v-slot:header>
        <div class="header-actions">
          <highlight-results-pdf
            :campaign="campaign"
            :loading="loading"
            :products="campaign.offer_sales"
            :campaign-cards-metrics="resultsCardsMetrics"
            :activatable="campaign.activatable"
          >
            <template v-slot:total-products-table>
              <products-results-table :metrics="productsData" type="total" />
            </template>
            <template v-slot:products-table>
              <products-results-table :metrics="productsData" type="products" />
            </template>
            <template v-slot:profile-table>
              <profile-results-table :metrics="funnelMetrics" base-metric="highlight" />
            </template>
          </highlight-results-pdf>
        </div>
      </template>
      <ResultsHeader
        :campaign="campaign"
        :highlight="true"
        :show-settings-button="true"
        offer-type="general"
        settingsButtonLabel="Ver configurações do destaque"
      />
      <v-row>
        <v-col v-for="card in resultsCardsMetrics" :key="card.title" cols="3">
          <cbm-card
            simple-card
            :title="card.title"
            :number-text="card.numberText"
            :number-color="card.numberColor || ''"
            :percent-text="card.percentText"
            :percentage-tooltip="card.percentageTooltip"
            :percent-description="card.percentDescription"
            :tooltip="card.tooltip"
            :boolean-loader="loading"
            :is-currency="card.isCurrency"
            :unsetPercentageColors="card.unsetPercentageColors"
            :quick-action="card.quickAction"
          >
            <template v-if="card.numberDescription" #custom-additional-value>
              <p class="mb-0 cashback-expiration">
                {{ card.numberDescription }}
              </p>
            </template>
          </cbm-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <funnel
            :campaign="campaign"
            :metrics="funnelMetrics"
            :headers="funnelHeaders"
            :baseMetric="funnelBaseMetric"
            :hideQuickActionFromFranchisee="false"
            :isMercapromo="false"
            :isHighlight="true"
            showQuickAction
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <products-table :isHighlight="true" :metrics="productsData" :campaign="productsCampaing" />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <results-units-table :items="unitsMetrics" />
        </v-col>
      </v-row>
      <mf-action-buttons
        :cancel-button="{
          text: 'Fechar',
          action: backToHighlightsList,
          isVisible: true,
          isDisabled: false,
        }"
      />
    </base-page-layout>
  </mf-loading-dialog>
</template>

<script>
import BasePageLayout from '@/components/BasePageLayout.vue'
import CbmCard from '@/components/CbmCard.vue'
import ResultsHeader from '@/components/campaigns/ResultsHeader.vue'
import { QUERY_GET_HIGHLIGHT_BY_ID } from '../../graphql'
import { apolloProvider } from '@/apollo-provider.js'
import { microservicesUrls } from '@/plugins/microservices/microservices'
import ResultsUnitsTable from '../components/results/UnitsTable.vue'
import { criteriaGroup, boughtFixedPeriodCriteria, viewedHighlightOffersCriteria } from '@/components/quick-actions/segmentationGroups.js'
import { DateTime } from 'luxon'

export default {
  name: 'Results',
  components: {
    BasePageLayout,
    ResultsHeader,
    CbmCard,
    Funnel: () => import('@/modules/campanhas/views/components/results/Funnel.vue'),
    ProductsTable: () => import('@/modules/campanhas/views/components/results/ProductsTable.vue'),
    ResultsUnitsTable,
    HighlightResultsPdf: () => import('@/modules/mobile/destaques/views/components/pdf/HighlightResultsPdf.vue'),
    ProductsResultsTable: () => import('@/modules/mobile/destaques/views/components/pdf/ProductsResultsTable.vue'),
    ProfileResultsTable: () => import('@/modules/mobile/destaques/views/components/pdf/ProfileResultsTable.vue'),
  },
  data() {
    return {
      loading: false,
      campaign: {},
      resultsCardsMetrics: [],
      funnelMetrics: {},
      funnelBaseMetric: 'highlight',
      productsData: {},
      productsCampaing: {},
      unitsMetrics: [],
      metricsEmpty: [],
    }
  },
  computed: {
    funnelHeaders() {
      return [
        { text: '', sortable: false },
        { text: 'Clientes', sortable: false, align: 'right' },
        {
          text: 'Percentual dos que visualizaram',
          value: 'received',
          sortable: false,
          align: 'right',
          tooltip: 'Considerando o total de clientes que visualizaram o destaque qual percentual desses fez compra.',
        },
        { text: 'Faturamento', sortable: false, align: 'right' },
        { text: 'Ticket médio', sortable: false, align: 'right' },
        { text: 'Cupons', sortable: false, align: 'right' },
      ]
    },
  },
  mounted() {
    this.loadHighlightMetrics()
  },
  methods: {
    loadHighlightMetrics() {
      this.loading = true
      const highlightedOfferId = this.$route.params.id

      this.fetchHighlight({ highlightedOfferId })
        .then(({ highlightedOffer }) => {
          this.campaign = this.mapCampaign(highlightedOffer)
          this.resultsCardsMetrics = this.mapResultsMetrics(highlightedOffer.metrics ?? this.getCardsEmptyValues())
          this.funnelMetrics = this.mapFunnelMetrics(highlightedOffer.metrics.resultsByProfile)
          this.productsData = this.mapProductsData(highlightedOffer.metrics?.productsMetrics, highlightedOffer.metrics?.productsTotalMetrics)
          this.productsCampaing = this.mapProductsCampaign(highlightedOffer)
          this.unitsMetrics = this.mapUnitsMetrics(highlightedOffer.metrics.unitsMetrics)
        })
        .catch((error) => {
          console.error('Erro ao buscar métricas:', error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    fetchHighlight(variables) {
      return apolloProvider.defaultClient
        .query({
          query: QUERY_GET_HIGHLIGHT_BY_ID,
          variables,
          fetchPolicy: 'no-cache',
          context: {
            uri: microservicesUrls['fedGateway'],
          },
        })
        .then(({ data }) => {
          if (!data.highlightedOffer) {
            throw new Error('Destaque não encontrado.')
          }
          return data
        })
        .catch((error) => {
          console.error('Erro na consulta getHighlight:', error)
          throw error
        })
    },
    backToHighlightsList() {
      this.$router.push({ name: 'highlight-list' }).catch(() => {})
    },
    mapCampaign(highlightedOffer) {
      return {
        _id: highlightedOffer.id,
        name: highlightedOffer.displayTitle,
        description: highlightedOffer.description,
        status: highlightedOffer.status,
        start_date: highlightedOffer.initialDate || '',
        end_date: highlightedOffer.finalDate || '',
        flash: false,
        smart: false,
        activatable: highlightedOffer.type === 'EXPANDED',
        units: (highlightedOffer.units || []).map((unit) => ({
          name: unit.name || '',
          id_erp: unit.id_erp || '',
          cnpj: unit.cnpj || '',
          address: unit.address || '',
          contact: unit.contact || '',
          ecommerce_v2_active: unit.ecommerce_v2_active || false,
          tags:
            unit.tags?.map((tag) => ({
              name: tag.name || '',
              color: tag.color || '',
            })) || [],
        })),
        offer_sales: highlightedOffer.products.map((p) => ({
          name: p.name,
          sku: p.sku,
          image_url: p.image,
        })),
      }
    },
    mapResultsMetrics(metrics) {
      const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0)
      const startDate = this.campaign.start_date
      const finalDate = this.campaign.final_date || this.campaign.end_date
      const strStartDate = DateTime.fromFormat(startDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
      const strFinalDate = DateTime.fromFormat(finalDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')

      const quickInitialDate = DateTime.fromFormat(startDate, 'yyyy-MM-dd').toFormat('yyyyMMdd')
      const quickFinalDate = DateTime.fromFormat(finalDate, 'yyyy-MM-dd').toFormat('yyyyMMdd')

      return [
        {
          title: 'Clientes que visualizaram',
          numberText: (Number(metrics.bigNumbers?.clientsViewedTotalUsers) || 0).toLocaleString('pt-BR'),
          percentText: metrics.bigNumbers?.clientsClickedTotalUsers ? metrics.bigNumbers.clientsClickedTotalUsers.toLocaleString('pt-BR') : '0',
          percentDescription: 'clientes que clicaram',
          unsetPercentageColors: true,
          quickAction: {
            id: 'viewed',
            shortDescription: `Clientes que visualizaram o destaque ${this.campaign.name} no período de mensuração`,
            longDescription: `Clientes que visualizaram o destaque ${this.campaign.name} no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
            segmentationGroup: () => criteriaGroup(viewedHighlightOffersCriteria(this.campaign._id)),
          },
        },
        {
          title: 'Visualizaram e compraram',
          numberText: (Number(metrics.bigNumbers?.clientsViewedAndPurchasedSomethingTotalUsers) || 0).toLocaleString('pt-BR'),
          tooltip: 'Quantidade de clientes que viram a campanha e realizaram pelo menos uma compra no período de mensuração.',
          quickAction: {
            id: 'viewed_and_bought',
            shortDescription: `Clientes que visualizaram o destaque ${this.campaign.name} e compraram no período de mensuração`,
            longDescription: `Clientes que visualizaram o destaque ${this.campaign.name} e compraram no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
            segmentationGroup: () =>
              criteriaGroup(
                boughtFixedPeriodCriteria(
                  this.campaign._id,
                  quickInitialDate,
                  quickFinalDate,
                  this.productsCampaing.unitsConfiguration.map((unit) => unit.id_erp)
                )
              ),
          },
        },
        {
          title: 'Ticket médio',
          numberText: formatCurrency(Number(metrics.bigNumbers?.clientsViewedAndPurchasedSomethingAverageTicket) || 0),
          percentText: `${
            metrics.bigNumbers?.clientsViewedAndPurchasedSomethingAverageTicketPercentage === 0
              ? '0,0%'
              : `${
                  metrics.bigNumbers?.clientsViewedAndPurchasedSomethingAverageTicketPercentage > 0 ? '+' : ''
                }${metrics.bigNumbers?.clientsViewedAndPurchasedSomethingAverageTicketPercentage.toFixed(1).replace('.', ',')}%`
          }`,
          percentDescription: 'do ticket médio das lojas',
          unsetPercentageColors: false,
          isCurrency: true,
          tooltip: 'Ticket médio dos clientes impactados pela campanha em comparação ao ticket médio total do clube no mesmo período.',
        },
        {
          title: 'Faturamento',
          numberText: formatCurrency(Number(metrics.bigNumbers?.clientsViewedAndPurchasedSomethingRevenue) || 0),
          percentText: metrics.bigNumbers?.clientsViewedAndPurchasedSomethingRevenuePercentage
            ? `${metrics.bigNumbers.clientsViewedAndPurchasedSomethingRevenuePercentage.toFixed(1).replace('.', ',')}%`
            : '0,0%',
          percentDescription: 'do faturamento total',
          unsetPercentageColors: true,
          isCurrency: true,
          tooltip: 'Faturamento dos cupons com produtos em oferta.',
        },
      ]
    },
    mapFunnelMetrics(profileMetrics) {
      const mapMetrics = (metrics) => ({
        count: metrics?.clients,
        representation: metrics?.clientsPercentage,
        revenue: metrics?.revenue,
        average_ticket: metrics?.averageTicket,
        tickets: metrics?.tickets,
      })
      return {
        clients_metrics: {
          viewed_and_bought: mapMetrics(profileMetrics.clientsViewedAndPurchasedSomethingMetrics),
          viewed_with_products: mapMetrics(profileMetrics.clientsViewedAndPurchasedProductsMetrics),
          viewed_with_only_products: mapMetrics(profileMetrics.clientsViewedAndPurchasedOnlyTheProductsMetrics),
        },
      }
    },
    mapProductsData(productsMetrics = [], productsTotalMetrics = {}) {
      const totals = productsMetrics.reduce(
        (acc, product) => {
          acc.quantity += product.quantity || 0
          acc.activated_clients += product.clients || 0
          acc.revenue += product.revenue || 0
          acc.tickets += product.tickets || 0
          acc.clients += product.clients || 0
          return acc
        },
        { quantity: 0, activated_clients: 0, revenue: 0, tickets: 0, clients: 0 }
      )
      return {
        products_metrics: {
          table: productsMetrics.map((product) => ({
            product: { sku: product.code, name: product.name },
            received: {
              quantity: product.quantity,
              activated_clients: product.clients,
              discounts: product.discount || 0,
              offers_schedule_discounts: 0,
              revenue: product.revenue,
              tickets: product.tickets,
              clients: product.clients,
              impactedPercentage: product.impactedPercentage,
              rebate: 0,
              average_discount: 0,
            },
            total: { quantity: product.quantity },
            earn: { total_value: 0, clients: product.clients },
            eligible: { quantity: 0 },
          })),
          totals: { received: totals, total: { quantity: totals.quantity } },
          productsTotalMetrics,
        },
        eligible_metrics: { quantity: 0 },
      }
    },
    mapProductsCampaign(highlightedOffer) {
      return {
        _id: highlightedOffer.id,
        title: highlightedOffer.title,
        initial_date: highlightedOffer.initialDate,
        final_date: highlightedOffer.finalDate,
        start_date: highlightedOffer.offersSchedule?.startDate,
        end_date: highlightedOffer.offersSchedule?.endDate,
        model: 'sales',
        unitsConfiguration: highlightedOffer.units?.map((unit) => ({ id_erp: unit.id_erp })) || [],
      }
    },
    mapUnitsMetrics(unitsMetrics = []) {
      return unitsMetrics.map((metric) => ({
        name: metric.name,
        revenue: metric.revenue,
        discount: metric.discount,
        clients: metric.clients,
        averageTicket: metric.averageTicket,
      }))
    },
    getCardsEmptyValues() {
      this.metricsEmpty = {
        bigNumbers: {
          clientsViewedTotalUsers: 0,
          clientsClickedTotalUsers: 0,
          clientsViewedAndPurchasedSomethingTotalUsers: 0,
          clientsViewedAndPurchasedSomethingAverageTicket: 0,
          clientsViewedAndPurchasedSomethingAverageTicketPercentage: 0,
          clientsViewedAndPurchasedSomethingRevenue: 0,
          clientsViewedAndPurchasedSomethingRevenuePercentage: 0,
        },
      }

      return this.metricsEmpty
    },
  },
}
</script>
